// Pages/OnboardingFinish.jsx
//
// Landing page for the "Finish Setup" link sent from WhatsApp chat once a
// user has given their first name and country in the conversation. This is
// the ONE web hop in an otherwise WhatsApp-native sign-up: a password can't
// be safely typed in chat (visible in history, and the WhatsApp Cloud API
// has no way to delete a message it received), and identity verification
// needs Sumsub's hosted document + selfie/liveness capture, which can't run
// inside WhatsApp either.
//
// Flow: validate the one-time token (prefill) -> collect password (+ optional
// email) -> POST /auth/onboarding/complete creates the account and logs the
// user in -> redirect to /kyc, which already knows how to run the right
// verification flow (Sumsub vs Bridge) for the user's country. WhatsApp is
// notified separately, server-side, once Sumsub approval lands (see
// NotificationService.notify_kyc_approved) — this page does not need to wait
// around for that.
import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { ShieldCheck, Eye, EyeOff, Loader2, MessageCircle } from "lucide-react";
import { useOnboardingPrefill, useCompleteOnboarding } from "../hooks/useOnboarding";

const INPUT_CLASS =
  "w-full rounded-2xl bg-white border border-slate-200 px-4 py-3.5 text-sm text-[#0A2540] placeholder:text-slate-400 focus:outline-none focus:border-[#0A4730] focus:ring-4 focus:ring-[#0A4730]/5 transition-all duration-200 shadow-sm";

// Mirrors the regular web signup (DetailsStep). The backend now requires these
// for every registration (profile + compliance records).
const GENDER_OPTIONS = [
  ["female", "Female"],
  ["male", "Male"],
  ["non_binary", "Non-binary"],
  ["other", "Other"],
  ["prefer_not_to_say", "Prefer not to say"],
];

const SOURCE_OF_FUNDS_OPTIONS = [
  ["salary", "Salary"],
  ["business_income", "Business income"],
  ["savings", "Savings"],
  ["investments", "Investments"],
  ["inheritance", "Inheritance"],
  ["pension", "Pension"],
  ["family_support", "Family support"],
  ["sale_of_assets", "Sale of assets"],
  ["other", "Other"],
];

const FieldLabel = ({ children }) => (
  <label className="block text-[10px] uppercase tracking-[0.18em] font-semibold text-[#0A4730]/70 mb-2">
    {children}
  </label>
);

function CenteredCard({ children }) {
  return (
    <div className="min-h-screen bg-[#FDFDFD] flex items-center justify-center px-5 py-10">
      <div className="bg-white rounded-3xl border border-slate-100 shadow-[0_4px_20px_-5px_rgba(0,0,0,0.05)] p-6 md:p-8 max-w-md w-full">
        {children}
      </div>
    </div>
  );
}

function InvalidLink() {
  return (
    <CenteredCard>
      <div className="text-center">
        <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-5">
          <MessageCircle size={28} className="text-red-500" />
        </div>
        <h2 className="text-lg font-semibold text-[#0A2540] mb-2">
          This link has expired
        </h2>
        <p className="text-sm text-[#0A2540]/50 leading-relaxed">
          Please go back to WhatsApp and send <b>Hi</b> to BluPay to get a new
          setup link.
        </p>
      </div>
    </CenteredCard>
  );
}

export function OnboardingFinish() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const { prefill, isLoadingPrefill, isPrefillError } = useOnboardingPrefill(token);
  const { completeOnboardingAsync, isCompleting, completeError } = useCompleteOnboarding();

  const [showPassword, setShowPassword] = useState(false);
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm();
  const password = watch("password");

  if (!token || isPrefillError) {
    return <InvalidLink />;
  }

  if (isLoadingPrefill) {
    return (
      <div className="min-h-screen bg-[#FDFDFD] flex items-center justify-center">
        <p className="text-sm text-[#0A2540]/40">Loading…</p>
      </div>
    );
  }

  async function onSubmit(data) {
    try {
      await completeOnboardingAsync({
        token,
        password: data.password,
        email: data.email,
        gender: data.gender,
        source_of_funds: data.sourceOfFunds,
      });
      navigate("/kyc", { replace: true });
    } catch {
      // completeError below already renders the message.
    }
  }

  return (
    <CenteredCard>
      <div className="text-center mb-6">
        <div className="w-16 h-16 rounded-full bg-[#E8F5E1] flex items-center justify-center mx-auto mb-5">
          <ShieldCheck size={28} className="text-[#0A4730]" />
        </div>
        <h1 className="text-2xl text-[#0A4730] font-serif font-semibold">
          Finish setting up{prefill?.full_name ? `, ${prefill.full_name}` : ""}
        </h1>
        <p className="text-sm text-[#0A2540]/50 mt-2">
          {prefill?.phone_masked
            ? `Setting up the account for ${prefill.phone_masked}`
            : "Set a password to secure your account."}
          {" "}Next you'll verify your identity — takes about 2 minutes.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <FieldLabel>Password</FieldLabel>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              placeholder="At least 8 characters"
              className={INPUT_CLASS}
              {...register("password", {
                required: "Password is required",
                minLength: { value: 8, message: "Must be at least 8 characters" },
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#0A4730]"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {errors.password && (
            <p className="text-xs text-red-500 mt-1.5">{errors.password.message}</p>
          )}
        </div>

        <div>
          <FieldLabel>Confirm password</FieldLabel>
          <input
            type={showPassword ? "text" : "password"}
            placeholder="Re-enter your password"
            className={INPUT_CLASS}
            {...register("confirmPassword", {
              required: "Please confirm your password",
              validate: (value) => value === password || "Passwords don't match",
            })}
          />
          {errors.confirmPassword && (
            <p className="text-xs text-red-500 mt-1.5">{errors.confirmPassword.message}</p>
          )}
        </div>

        <div>
          <FieldLabel>Email</FieldLabel>
          <input
            type="email"
            placeholder="you@example.com"
            className={INPUT_CLASS}
            {...register("email", {
              required: "Email is required",
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Enter a valid email address",
              },
            })}
          />
          {errors.email && (
            <p className="text-xs text-red-500 mt-1.5">{errors.email.message}</p>
          )}
        </div>

        <div>
          <FieldLabel>Gender</FieldLabel>
          <select
            className={INPUT_CLASS}
            defaultValue=""
            {...register("gender", { required: "Select a gender option" })}
          >
            <option value="" disabled>Select…</option>
            {GENDER_OPTIONS.map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          {errors.gender && (
            <p className="text-xs text-red-500 mt-1.5">{errors.gender.message}</p>
          )}
        </div>

        <div>
          <FieldLabel>Source of funds</FieldLabel>
          <select
            className={INPUT_CLASS}
            defaultValue=""
            {...register("sourceOfFunds", { required: "Select a source of funds" })}
          >
            <option value="" disabled>Select…</option>
            {SOURCE_OF_FUNDS_OPTIONS.map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          {errors.sourceOfFunds && (
            <p className="text-xs text-red-500 mt-1.5">{errors.sourceOfFunds.message}</p>
          )}
        </div>

        {completeError && (
          <div className="rounded-2xl bg-red-50 border border-red-100 p-4 text-red-500 text-sm">
            {typeof completeError === "string"
              ? completeError
              : "We couldn't finish setting up your account. Please try again."}
          </div>
        )}

        <button
          type="submit"
          disabled={isCompleting}
          className="w-full h-14 rounded-2xl bg-[#0A4730] text-white font-bold tracking-wide hover:bg-[#0A4730]/90 transition-all duration-300 active:scale-[0.98] disabled:opacity-60 flex items-center justify-center gap-2"
        >
          {isCompleting && <Loader2 size={16} className="animate-spin" />}
          {isCompleting ? "Setting up…" : "Continue to identity verification"}
        </button>
      </form>

      <p className="text-xs text-[#0A2540]/40 text-center mt-6">
        Already have an account?{" "}
        <Link to="/login" className="text-[#0A4730] font-semibold">
          Log in
        </Link>
      </p>
    </CenteredCard>
  );
}
